import { useCallback, useRef } from 'react'
import { X } from 'lucide-react'
import type { Tool } from '../../types'
import ToolLibraryEditor from '../mission-control/ToolLibraryEditor'
import { useWorkspaceEvents } from '../../contexts/WorkspaceEventContext'

interface ToolCribPanelProps {
  tools: Tool[]
  operationType: 'milling' | 'turning'
  onToolsChange: (tools: Tool[]) => void
  onClose: () => void
}

export default function ToolCribPanel({ tools, operationType, onToolsChange, onClose }: ToolCribPanelProps) {
  const { emit } = useWorkspaceEvents()
  const prevToolsRef = useRef(tools)

  const handleToolsChange = useCallback((newTools: Tool[]) => {
    const oldTools = prevToolsRef.current

    // Detect added tools
    for (const t of newTools) {
      if (!oldTools.find(o => o.number === t.number)) {
        emit({ type: 'tool_added', toolNumber: t.number, toolType: t.type, diameter: t.diameter })
      }
    }
    // Detect removed tools
    for (const t of oldTools) {
      if (!newTools.find(n => n.number === t.number)) {
        emit({ type: 'tool_removed', toolNumber: t.number, description: t.description })
      }
    }
    // Detect modified tools
    for (const t of newTools) {
      const old = oldTools.find(o => o.number === t.number)
      if (old) {
        const changes: string[] = []
        if (old.type !== t.type) changes.push(`type: ${old.type}→${t.type}`)
        if (old.diameter !== t.diameter) changes.push(`dia: ${old.diameter}→${t.diameter}`)
        if (old.stickout !== t.stickout) changes.push(`stickout: ${old.stickout}→${t.stickout}`)
        if (old.flutes !== t.flutes) changes.push(`flutes: ${old.flutes}→${t.flutes}`)
        if (changes.length > 0) {
          emit({ type: 'tool_modified', toolNumber: t.number, changes: changes.join(', ') })
        }
      }
    }

    prevToolsRef.current = newTools
    onToolsChange(newTools)
  }, [onToolsChange, emit])
  return (
    <div
      className="rounded-xl w-[520px] p-4 space-y-4 border border-cyan-500/20"
      style={{ background: '#0d1526', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase block">Tool Crib</span>
          <span className="font-mono text-xs text-slate-300 capitalize">{operationType}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] text-cyan-500/70">
            {tools.length} {tools.length === 1 ? 'tool' : 'tools'}
          </span>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Tool table */}
      <div className="max-h-[60vh] overflow-y-auto">
        <ToolLibraryEditor
          tools={tools}
          operationType={operationType}
          onChange={handleToolsChange}
        />
      </div>
    </div>
  )
}
